import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link as MuiLink,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useDocData from "../../../Hooks/useDocData";
import { resolveArtistApiBaseUrl } from "../../../utils/artistApi";

const ArtistInventory = () => {
  const { artistName } = useParams();
  const [artist, setArtist] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const docData = useDocData();
  const docArtists = useMemo(() => docData[0] || [], [docData]);

  const apiBaseUrl = useMemo(() => resolveArtistApiBaseUrl(), []);

  const decodedArtistName = useMemo(() => {
    try {
      return decodeURIComponent(artistName);
    } catch (err) {
      return artistName;
    }
  }, [artistName]);

  const displayName = artist?.name || decodedArtistName;
  const instagramHandle = useMemo(() => {
    if (artist?.instagram) {
      return String(artist.instagram).trim();
    }
    const normalizedName = String(decodedArtistName || "").trim().toLowerCase();
    if (!normalizedName) return "";
    const match = docArtists.find((entry) => {
      const nameMatch =
        String(entry?.name || "").trim().toLowerCase() === normalizedName;
      const usernameMatch =
        String(entry?.username || "").trim().toLowerCase() === normalizedName;
      return nameMatch || usernameMatch;
    });
    return String(match?.instagram || "").trim();
  }, [artist?.instagram, decodedArtistName, docArtists]);
  const artistBlurb = useMemo(() => {
    if (artist?.blurb) {
      return String(artist.blurb).trim();
    }
    const normalizedName = String(decodedArtistName || "").trim().toLowerCase();
    if (!normalizedName) return "";
    const match = docArtists.find((entry) => {
      const nameMatch =
        String(entry?.name || "").trim().toLowerCase() === normalizedName;
      const usernameMatch =
        String(entry?.username || "").trim().toLowerCase() === normalizedName;
      return nameMatch || usernameMatch;
    });
    return String(match?.blurb || "").trim();
  }, [artist?.blurb, decodedArtistName, docArtists]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError("");

    const fetchItems = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/public/artists/${encodeURIComponent(decodedArtistName)}/items`
        );
        if (!response.ok) {
          throw new Error("Failed to load items");
        }
        const data = await response.json();
        if (!isMounted) return;
        setArtist(data.artist || null);
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (err) {
        if (!isMounted) return;
        setError("We couldn't load this artist's inventory right now.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchItems();
    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl, decodedArtistName]);

  const formatPrice = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") {
      return value > 0 ? `$${(value / 100).toFixed(2)}` : null;
    }
    const digits = String(value).replace(/\D/g, "");
    if (!digits) return null;
    const cents = Number(digits);
    if (!Number.isFinite(cents) || cents <= 0) return null;
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getOptimizedImage = (src, size) => {
    if (!src) return "";
    if (src.includes("res.cloudinary.com") && src.includes("/upload/")) {
      const transformation = `f_auto,q_auto,w_${size},h_${size},c_fill`;
      return src.replace("/upload/", `/upload/${transformation}/`);
    }
    return src;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const categories = useMemo(() => {
    const categorySet = new Set();
    items.forEach((item) => {
      if (item.category) {
        categorySet.add(item.category);
      }
    });
    return ["all", ...Array.from(categorySet).sort()];
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const name = String(item.name || "").toLowerCase();
      const matchesName = !normalizedSearch || name.includes(normalizedSearch);
      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;
      return matchesName && matchesCategory;
    });
  }, [items, normalizedSearch, categoryFilter]);

  return (
    <Box
      sx={{
        py: 8,
        background: "linear-gradient(180deg, #e8f2ff 0%, #f5faff 100%)",
        minHeight: "100vh",
      }}
    >
      <Helmet>
        <title>{`${displayName} | Artist Alley Inventory`}</title>
        <meta
          name="description"
          content={`Browse items from ${displayName} in Den Den Claw's Artist Alley.`}
        />
        <link
          rel="canonical"
          href={`https://www.dendenclaw.com/explore/artist-alley/${encodeURIComponent(
            decodedArtistName
          )}`}
        />
      </Helmet>

      <Container maxWidth="lg">
        <Button
          component={Link}
          to="/explore/artist-alley"
          sx={{ mb: 4 }}
        >
          Back to Artist Alley
        </Button>

        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {displayName}
          </Typography>
          {instagramHandle && (
            <Typography color="text.secondary">
              <MuiLink
                href={`https://instagram.com/${encodeURIComponent(instagramHandle)}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="inherit"
              >
                @{instagramHandle}
              </MuiLink>
            </Typography>
          )}
          {artistBlurb && (
            <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
              {artistBlurb}
            </Typography>
          )}
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && !error && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 4 }}
          >
            <TextField
              fullWidth
              label="Search items"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="artist-category-label">Category</InputLabel>
              <Select
                labelId="artist-category-label"
                label="Category"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category === "all" ? "All categories" : category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        {!loading && error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && filteredItems.length === 0 && (
          <Typography color="text.secondary">
            No matching items yet.
          </Typography>
        )}

        {!loading && filteredItems.length > 0 && (
          <Grid container spacing={3}>
            {filteredItems.map((item) => {
              const priceText = formatPrice(item.priceCents ?? item.price);
              const rawImageUrl = item.imageUrl || item.image || item.photoUrl;
              const imageUrl = getOptimizedImage(rawImageUrl, 600);
              return (
                <Grid item xs={12} sm={6} md={4} key={item.id || item.name}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: 6,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        aspectRatio: "1 / 1",
                        bgcolor: "secondary.main",
                      }}
                    >
                      {imageUrl ? (
                        <CardMedia
                          component="img"
                          image={imageUrl}
                          alt={item.name || "Artist item"}
                          loading="lazy"
                          decoding="async"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            fontWeight: 600,
                          }}
                        >
                          Image coming soon
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          px: 1.25,
                          py: 1,
                          background:
                            "linear-gradient(180deg, rgba(8, 20, 35, 0.08) 0%, rgba(8, 20, 35, 0.62) 55%, rgba(8, 20, 35, 0.76) 100%)",
                          backdropFilter: "blur(2px)",
                          color: "#fff",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          title={item.name || "Item"}
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.15,
                            mb: 0.6,
                            textShadow: "0 1px 3px rgba(0,0,0,0.45)",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.name || "Item"}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.7}
                          sx={{ flexWrap: "wrap", mt: 1 }}
                        >
                          {priceText && (
                            <Chip
                              label={priceText}
                              size="small"
                              sx={{
                                height: 22,
                                bgcolor: "primary.main",
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            />
                          )}
                          {item.category && (
                            <Chip
                              label={item.category}
                              size="small"
                              sx={{
                                height: 22,
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "#fff",
                                backdropFilter: "blur(1px)",
                              }}
                            />
                          )}
                          {typeof item.stockCount === "number" && (
                            <Chip
                              label={`Stock: ${item.stockCount}`}
                              size="small"
                              sx={{
                                height: 22,
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "#fff",
                                backdropFilter: "blur(1px)",
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default ArtistInventory;
